import plur from 'plur'
import {useContext, useMemo, useState} from 'react'
import {shallowEqual} from 'react-redux'

import {DisabledContext} from '../../../../contexts/DisabledContext'
import {usePipelineId} from '../../../../hooks/pipeline'

import {getDraft} from 'src/components/pages/PipelinesPages/EditPipelinePage/EditPipelinePage.selectors'
import {
  collapsedBlocks,
  pipelineDraft,
} from 'src/components/pages/PipelinesPages/EditPipelinePage/slices/EditPipelinePage.slices'
import {useAppDispatch, useAppSelector} from 'src/hooks/react-redux'
import {getEmptyHash} from 'src/utils/empty'

const settingsBlockId = 'secrets'

function usePipelineSecrets() {
  const dispatch = useAppDispatch()
  const id = usePipelineId()
  const stateSecrets: Record<string, string | undefined> = useAppSelector(
    state => getDraft(state, id)?.settings.secrets ?? getEmptyHash(),
  )
  const renamedSecrets = useAppSelector(
    state => state.pipelines.pipelineDraft[id]?.renamed?.secrets,
  )
  const keys = useMemo(() => Object.keys(stateSecrets), [stateSecrets])
  const secrets = useMemo(
    () =>
      keys.map(key => {
        const name = renamedSecrets?.[key] ?? key

        return [key, name]
      }),
    [keys, renamedSecrets],
  )
  const deletedSecrets = useAppSelector(
    state =>
      state.pipelines.pipelineDraft[id]?.deleted?.secrets?.map(secretKey => {
        if (renamedSecrets?.[secretKey]) {
          return renamedSecrets[secretKey]
        }

        return secretKey
      }),
    shallowEqual,
  )
  const secretsCount = keys.filter(key => !deletedSecrets?.includes(key)).length
  const settingsBlockTitle = secretsCount ? plur('Secret', secretsCount) : 'No Secrets'

  const [isShowPipelineSecretForm, setIsShowPipelineSecretForm] = useState(false)
  const handlerAddSecretButtonClick = () => {
    setIsShowPipelineSecretForm(true)
    dispatch(collapsedBlocks.actions.toggle({id: settingsBlockId, collapsed: false}))
  }
  const handlerCloseSecretButtonClick = () => setIsShowPipelineSecretForm(false)
  const handlerApplySecretButtonClick = (name: string, value: string) => {
    dispatch(
      pipelineDraft.actions.setSecret({
        id,
        name,
        value,
      }),
    )
    setIsShowPipelineSecretForm(false)
  }
  const isDisabled = useContext(DisabledContext)
  const isSettingsBlockCollapsible = secrets.length > 0 || isShowPipelineSecretForm

  return {
    handlerApplySecretButtonClick,
    handlerCloseSecretButtonClick,
    handlerAddSecretButtonClick,
    isShowPipelineSecretForm,
    isSettingsBlockCollapsible,
    settingsBlockTitle,
    secrets,
    isDisabled,
    deletedSecrets,
    settingsBlockId,
  }
}

export default usePipelineSecrets
